import { Link } from "react-router-dom";
import { Shield, BookOpen, ClipboardCheck, Award, Brain, Scale } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function Landing() {
  const { user } = useAuth();
  return (
    <div className="bg-white">
      {/* Hero */}
      <section className="relative border-b border-slate-200 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{ backgroundImage: "url(https://images.unsplash.com/photo-1650136865959-0762ea47e5eb)" }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-white/90 to-white" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <div className="max-w-3xl">
            <img src="/logo.jpg" alt="Blue Line Security Consulting" className="h-20 w-auto mb-8" data-testid="landing-logo" />
            <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-blue-700 mb-6">
              <Shield className="h-4 w-4" />
              California Department of Justice — Penal Code §26165
            </div>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900"
              style={{ fontFamily: "Chivo, sans-serif" }}
            >
              California Concealed Carry Weapon (CCW) Online Training
            </h1>
            <p className="mt-6 text-lg text-slate-700 max-w-2xl leading-relaxed">
              An 8-hour curriculum covering firearm safety, shooting fundamentals, California legal use of force,
              sensitive-place rules under SB&nbsp;2, mandatory mental health awareness, and tactical de-escalation.
              Module quizzes, a comprehensive final exam, and a printable signed acknowledgement letter.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              {user && user !== false ? (
                <Link to="/dashboard" data-testid="cta-dashboard"
                  className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm px-8 py-4 font-medium transition-colors">
                  Continue to Dashboard
                </Link>
              ) : (
                <>
                  <Link to="/register" data-testid="cta-register"
                    className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm px-8 py-4 font-medium transition-colors">
                    Enroll Now
                  </Link>
                  <Link to="/login" data-testid="cta-login"
                    className="bg-white text-slate-900 border border-slate-300 hover:bg-slate-50 rounded-sm px-8 py-4 font-medium transition-colors">
                    Sign In
                  </Link>
                </>
              )}
            </div>
            <div className="mt-12 grid grid-cols-3 gap-6 max-w-lg">
              <Stat n="8h" label="Total instruction" />
              <Stat n="6" label="Modules + quizzes" />
              <Stat n="25" label="Final exam questions" />
            </div>
          </div>
        </div>
      </section>

      {/* Curriculum */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="border-b border-slate-200 pb-4 mb-12">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Curriculum</div>
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 mt-2" style={{ fontFamily: "Chivo, sans-serif" }}>
            Six modules, end-to-end coverage
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Topic icon={Shield} title="Firearm Safety & Handling" detail="Universal rules, semi-auto & revolver loading/unloading, malfunctions, CA safe storage." />
          <Topic icon={BookOpen} title="Shooting Fundamentals" detail="Grip, stance, sight alignment, trigger press, follow-through, recoil management." />
          <Topic icon={Scale} title="Legal Use of Force" detail="CA Penal Code, reasonable person standard, defense of others, post-incident actions." />
          <Topic icon={ClipboardCheck} title="Carry, Transport & Prohibited Places" detail="Listed firearms, vehicle transport, SB 2 sensitive places, federal restrictions." />
          <Topic icon={Brain} title="Mental Health Awareness" detail="State-mandated 1-hour block. Crisis recognition, 988, safe-storage during crisis." />
          <Topic icon={Award} title="Tactical Awareness & De-escalation" detail="Cooper's Color Code, managing unknown contacts, drawing hierarchy." />
        </div>
      </section>

      {/* How it works */}
      <section className="bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="border-b border-slate-200 pb-4 mb-12">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Course Flow</div>
            <h2 className="text-3xl font-bold tracking-tight text-slate-900 mt-2" style={{ fontFamily: "Chivo, sans-serif" }}>
              How the eight hours are structured
            </h2>
          </div>
          <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Step n="01" t="Read each module" d="Module timer enforces the full minimum study time before quiz unlocks." />
            <Step n="02" t="Pass the quiz" d="5 questions per module. 80% minimum. Retake until you pass." />
            <Step n="03" t="Final exam" d="25 questions covering the entire curriculum. 80% minimum." />
            <Step n="04" t="Sign & print" d="Receive an 8-hour completion acknowledgement letter with your signature." />
          </ol>
        </div>
      </section>

      <footer className="border-t border-slate-200 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-xs text-slate-500 leading-relaxed">
          This course is an educational training tool. It is not legal advice. Curriculum reflects California requirements
          referenced at <span className="font-mono">oag.ca.gov/firearms/ccwcert</span>. Verify current sensitive-place rules
          and your issuing agency&apos;s specific requirements before relying on any section of this material.
        </div>
      </footer>
    </div>
  );
}

function Stat({ n, label }) {
  return (
    <div className="border-l-2 border-slate-900 pl-4">
      <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>{n}</div>
      <div className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mt-1">{label}</div>
    </div>
  );
}

function Topic({ icon: Icon, title, detail }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-6 hover:border-slate-900 transition-colors">
      <Icon className="h-6 w-6 text-blue-700 mb-4" />
      <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>{title}</h3>
      <p className="mt-2 text-sm text-slate-600 leading-relaxed">{detail}</p>
    </div>
  );
}

function Step({ n, t, d }) {
  return (
    <li className="bg-white border border-slate-200 rounded-sm p-6">
      <div className="font-mono text-xs text-blue-700 font-semibold">{n}</div>
      <div className="text-base font-semibold text-slate-900 mt-3" style={{ fontFamily: "Chivo, sans-serif" }}>{t}</div>
      <p className="text-sm text-slate-600 mt-2 leading-relaxed">{d}</p>
    </li>
  );
}
